import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Play, Plus, Check, Headphones, Share2, Globe, Clock, Calendar, Mic, ChevronRight, Ban } from 'lucide-react';
import { Dialog, DialogContent } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useApp, fmtDuration } from '../context/AppContext';
import { OriginalBadge } from './ContentCard';
import { HeroTitle } from './Hero';
import { toast } from 'sonner';

function EpisodeRow({ ep, index, onPlay, image, progressPct, playing, isAudio }) {
  return (
    <button onClick={onPlay} className="w-full flex items-center gap-4 p-2.5 rounded-lg hover:bg-white/[0.04] text-left group transition-colors" data-testid={`episode-${ep.id}`}>
      <span className="w-6 text-center text-[13px] text-[#8A8694] font-medium">{index}</span>
      <div className="relative w-[128px] h-[72px] rounded-md overflow-hidden flex-none">
        <img src={image} alt="" className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-black/30 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity">
          <span className="w-9 h-9 rounded-full bg-white text-black flex items-center justify-center">{isAudio ? <Headphones size={15} /> : <Play size={15} fill="currentColor" className="ml-0.5" />}</span>
        </div>
        {progressPct > 0 && <div className="absolute left-0 right-0 bottom-0 h-[3px] bg-[#15121B]/25"><div className="h-full progress-fill" style={{ width: `${progressPct}%` }} /></div>}
        {playing && <div className="absolute top-1.5 left-1.5 eq h-4 flex items-end"><span /><span /><span /></div>}
      </div>
      <div className="min-w-0 flex-1">
        <div className="flex items-center justify-between gap-3">
          <div className="text-[14px] font-semibold truncate">{ep.title}</div>
          <div className="text-[12px] text-[#C7C4CB] flex-none">{fmtDuration(ep.duration)}</div>
        </div>
        <p className="text-[12.5px] text-[#C7C4CB] leading-snug line-clamp-2 mt-1">{ep.description}</p>
      </div>
    </button>
  );
}

export default function DetailModal() {
  const navigate = useNavigate();
  const { detailContent: c, closeDetail, toggleMyList, inMyList, following, toggleFollow, progress, isAvailable, audio, playEpisode, country } = useApp();
  const [season, setSeason] = useState('1');
  if (!c) return null;
  const listed = inMyList(c.id);
  const available = isAvailable(c);
  const isShow = c.type === 'show';
  const isPod = c.type === 'podcast';
  const seasonObj = isShow ? c.seasons.find((s) => String(s.number) === season) || c.seasons[0] : null;
  const prog = progress[c.id];

  const go = (path) => { closeDetail(); navigate(path); };
  const onPlay = () => {
    if (!available) { toast.error(`Not available in ${country}. This title is licensed for: ${c.rights.territories.join(', ')}`); return; }
    if (isPod) { playEpisode(c.id, c.episodes[0]); toast.success(`Now playing: ${c.episodes[0].title}`); return; }
    go(`/watch/${c.id}${prog?.episodeId ? `?ep=${prog.episodeId}` : ''}`);
  };

  return (
    <Dialog open={!!c} onOpenChange={(o) => !o && closeDetail()}>
      <DialogContent className="max-w-[880px] w-[94vw] p-0 bg-[#15121B] border-[#26212F] text-white overflow-hidden rounded-2xl gap-0 max-h-[92vh] overflow-y-auto no-scrollbar [&>button]:bg-black/60 [&>button]:rounded-full [&>button]:p-1.5 [&>button]:opacity-100 [&>button]:z-20" data-testid="detail-modal">
        <div className="relative aspect-[16/7.2] w-full">
          <img src={c.banner || c.image} alt="" className="absolute inset-0 w-full h-full object-cover" style={{ objectPosition: 'center 25%' }} />
          <div className="absolute inset-0 hero-shade-left" />
          <div className="absolute inset-0" style={{ background: 'linear-gradient(0deg, #0D111A 0%, rgba(13,17,26,0.6) 25%, transparent 55%)' }} />
          <div className="absolute left-8 bottom-6 right-8">
            {(c.isOriginal || isPod) && <OriginalBadge kind={isPod ? 'PODCAST' : 'ORIGINAL'} className="!text-[11px] [&_img]:!h-[11px] mb-2" />}
            <HeroTitle title={c.title} size={0.62} />
            <div className="mt-4 flex flex-wrap items-center gap-3">
              <button className={`btn-play ${!available ? 'opacity-60' : ''}`} onClick={onPlay} data-testid="detail-play">
                {available ? (isPod ? <Headphones size={16} /> : <Play size={16} fill="currentColor" />) : <Ban size={16} />}
                {!available ? 'Unavailable in your region' : prog ? 'Resume' : isPod ? 'Play Latest' : 'Play'}
              </button>
              <button className="btn-ghost" onClick={() => { toggleMyList(c.id); toast.success(listed ? 'Removed from My List' : 'Added to My List'); }} data-testid="detail-mylist">{listed ? <Check size={17} /> : <Plus size={17} />} My List</button>
              {isPod && <button className={`btn-ghost ${following.includes(c.id) ? '!border-[#8B2CFF] !bg-[#8B2CFF]/15' : ''}`} onClick={() => toggleFollow(c.id)} data-testid="detail-follow">{following.includes(c.id) ? 'Following' : 'Follow'}</button>}
              <button className="icon-btn !w-[42px] !h-[42px]" onClick={() => { navigator.clipboard?.writeText(`${window.location.origin}/?title=${c.id}`); toast.success('Link copied'); }} aria-label="Share"><Share2 size={16} /></button>
            </div>
          </div>
        </div>

        <div className="px-8 pb-8 pt-2 grid grid-cols-1 md:grid-cols-[1fr_260px] gap-8">
          <div>
            <div className="flex flex-wrap items-center gap-2.5 text-[13px] text-[#C7C4CB]">
              {c.match && <span className="text-[#4ADE80] font-semibold">{c.match}% Match</span>}
              <span>{c.year}</span>
              <span className="px-1.5 py-[1px] border border-[#3A3346] rounded text-[11px]">{c.rating}</span>
              {c.duration && <span>{fmtDuration(c.duration)}</span>}
              {isShow && <span>{c.seasons.length} Season{c.seasons.length > 1 ? 's' : ''}</span>}
              {isPod && <span>{c.episodes.length} Episodes</span>}
              <span className="px-1.5 py-[1px] bg-white/[0.06] rounded text-[11px]">HD</span>
            </div>
            {c.tagline && <p className="mt-3 text-[15px] font-semibold text-white">{c.tagline}</p>}
            <p className="mt-2 text-[14px] leading-relaxed text-[#C7C4CB]">{c.description}</p>
            {!available && (
              <div className="mt-4 flex items-start gap-3 p-3 rounded-lg bg-[#FF6B81]/10 border border-[#FF6B81]/30 text-[13px]">
                <Globe size={16} className="mt-0.5 text-[#FF6B81]" />
                <div><span className="font-semibold">Not available in your territory ({country}).</span><div className="text-[#C7C4CB] mt-0.5">Rights held by {c.rights.owner} &bull; {c.rights.type} &bull; {c.rights.territories.join(', ')}</div></div>
              </div>
            )}
          </div>
          <div className="text-[13px] space-y-2.5">
            {isPod ? (
              <div className="flex items-center gap-3"><img src={c.hostAvatar} alt="" className="w-10 h-10 rounded-full object-cover" /><div><div className="text-[#8A8694] text-[11px] uppercase tracking-wide">Host</div><div className="font-medium">{c.host}</div></div></div>
            ) : (
              <div><span className="text-[#8A8694]">Cast: </span><span className="text-[#C7C4CB]">{c.cast?.join(', ')}</span></div>
            )}
            {c.director && <div><span className="text-[#8A8694]">Director: </span><span className="text-[#C7C4CB]">{c.director}</span></div>}
            <div><span className="text-[#8A8694]">Genres: </span><span className="text-[#C7C4CB]">{c.genres.join(', ')}</span></div>
            <div><span className="text-[#8A8694]">Language: </span><span className="text-[#C7C4CB]">{c.language}</span></div>
            <div><span className="text-[#8A8694]">Distribution: </span><span className="text-[#C7C4CB]">{c.rights?.type}</span></div>
            {isPod && <button onClick={() => go(`/podcasts/${c.id}`)} className="mt-2 inline-flex items-center gap-1 text-[#8B2CFF] hover:text-[#FFFFFF] font-medium">Open podcast page <ChevronRight size={14} /></button>}
          </div>
        </div>

        {isShow && (
          <div className="px-8 pb-8" data-testid="detail-episodes">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-[17px] font-bold">Episodes</h3>
              <Select value={season} onValueChange={setSeason}>
                <SelectTrigger className="w-[150px] h-9 bg-[#1E1927] border-[#26212F] text-white" data-testid="season-select"><SelectValue /></SelectTrigger>
                <SelectContent className="bg-[#1E1927] border-[#26212F] text-white">
                  {c.seasons.map((s) => <SelectItem key={s.number} value={String(s.number)} className="focus:bg-[#2A2136] focus:text-[#FFFFFF]">Season {s.number}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div className="divide-y divide-[#26212F]">
              {seasonObj.episodes.map((ep) => (
                <EpisodeRow key={ep.id} ep={ep} index={ep.number} image={c.image} progressPct={prog?.episodeId === ep.id ? Math.round((prog.position / prog.duration) * 100) : 0}
                  onPlay={() => { if (!available) { toast.error('Not available in your region'); return; } go(`/watch/${c.id}?ep=${ep.id}`); }} />
              ))}
            </div>
          </div>
        )}

        {isPod && (
          <div className="px-8 pb-8" data-testid="detail-podcast-episodes">
            <h3 className="text-[17px] font-bold mb-3 flex items-center gap-2"><Mic size={16} /> Latest Episodes</h3>
            <div className="divide-y divide-[#26212F]">
              {c.episodes.slice(0, 4).map((ep) => (
                <EpisodeRow key={ep.id} ep={ep} index={ep.number} image={ep.image} isAudio playing={audio.episode?.id === ep.id && audio.playing} onPlay={() => { playEpisode(c.id, ep); }} />
              ))}
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
